import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

type AppVersionState = {
  /** A newer build of this app + env is live; apply it on the next navigation. */
  isUpdatePending: boolean;
  /** Whether the notice is on screen. Hiding it never disarms the update. */
  isNoticeVisible: boolean;
  /** A mutation is in flight — hold the refresh so no write is aborted. */
  isWriting: boolean;
  /** Set once the document has committed to a full load. */
  isRefreshing: boolean;
  actions: {
    /** Arm the update and show the notice. Repeat calls are no-ops. */
    markUpdatePending: () => void;
    /** "Keep working" — the notice goes, the update stays armed. */
    hideNotice: () => void;
    setWriting: (isWriting: boolean) => void;
    /** Returns true for the first caller only — the one that owns the reload. */
    acquireRefresh: () => boolean;
  };
};

export const useAppVersionStore = create<AppVersionState>()(
  devtools(
    immer((set, get) => ({
      isUpdatePending: false,
      isNoticeVisible: false,
      isWriting: false,
      isRefreshing: false,
      actions: {
        markUpdatePending: () => {
          if (get().isUpdatePending) return;
          set(
            (state) => {
              state.isUpdatePending = true;
              state.isNoticeVisible = true;
            },
            false,
            "appVersion/markUpdatePending",
          );
        },

        hideNotice: () => {
          set(
            (state) => {
              state.isNoticeVisible = false;
            },
            false,
            "appVersion/hideNotice",
          );
        },

        setWriting: (isWriting) => {
          set(
            (state) => {
              state.isWriting = isWriting;
            },
            false,
            {
              type: "appVersion/setWriting",
              payload: { isWriting },
            },
          );
        },

        acquireRefresh: () => {
          if (get().isRefreshing) return false;
          set(
            (state) => {
              state.isRefreshing = true;
            },
            false,
            "appVersion/acquireRefresh",
          );
          return true;
        },
      },
    })),
    { name: "AppVersionStore" },
  ),
);
